import { useEffect } from 'react';

/**
 * Minimal SEO hook: sets document.title and meta tags without a library.
 * Also injects JSON-LD schemas (FAQ, HowTo, Article) if provided.
 */
export function useSEO({ title, description, keywords, canonical, schemas = [] }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content, isProperty = false) => {
      if (!content) return null;
      const sel = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.head.querySelector(sel);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        if (isProperty) el.setAttribute('property', name);
        else el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return { el, created };
    };

    const created = [];
    [
      setMeta('description', description),
      setMeta('keywords', keywords),
      setMeta('og:title', title, true),
      setMeta('og:description', description, true),
      setMeta('twitter:title', title),
      setMeta('twitter:description', description),
    ].forEach((r) => {
      if (r?.created) created.push(r.el);
    });

    // Canonical link
    let canonicalEl = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      const wasCreated = !canonicalEl;
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
      if (wasCreated) created.push(canonicalEl);
    }

    // JSON-LD schemas
    const schemaEls = schemas.map((schema) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(schema);
      s.setAttribute('data-vd-schema', '1');
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
      schemaEls.forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, schemas]);
}

export function buildFAQSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function buildHowToSchema(toolName, steps) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to ${toolName.toLowerCase()}`,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.t,
      text: s.d,
    })),
  };
}

export function buildArticleSchema(post, base) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.published_at,
    author: { '@type': 'Organization', name: 'ValutDocu' },
    publisher: { '@type': 'Organization', name: 'ValutDocu' },
    mainEntityOfPage: `${base}/blog/${post.slug}`,
  };
}
